/**
 * @file usePatients.js
 *
 * Patient Hooks
 *
 * Same pattern as useProfessionals.js but for patient data.
 * Three hooks for three use cases:
 *   usePatients()       — list all patients (for professionals/admin)
 *   usePatientProfile() — logged-in patient's own profile (for editing)
 *   usePatientDetail()  — view one specific patient (by ID)
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { patientService } from "../services";
import { PATIENT_STRINGS } from "../constants/strings";

/**
 * Hook to fetch and hold the list of all patients (professional/admin view).
 *
 * @param {Object} [options]
 * @param {boolean} [options.autoFetch=true] - Whether to fetch on mount
 * @returns {{patients: Array, isLoading: boolean, error: Error|null, refresh: Function}}
 */
export function usePatients(options = {}) {
  const { autoFetch = true } = options;
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPatients = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await patientService.getAll();
      setPatients(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch) fetchPatients();
  }, [autoFetch, fetchPatients]);

  return { patients, isLoading, error, refresh: fetchPatients };
}

/**
 * Hook for the logged-in patient to view and edit their own profile.
 *
 * @returns {{profile: Object|null, isLoading: boolean, error: Error|null, updateProfile: Function, refresh: Function}}
 */
export function usePatientProfile() {
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await patientService.getMe();
      setProfile(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Send updated data to API, then update local state with server response
  const updateProfile = async (data) => {
    const response = await patientService.updateMe(data);
    setProfile(response.data);
    toast.success(PATIENT_STRINGS.SAVED);
    return response.data;
  };

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return { profile, isLoading, error, updateProfile, refresh: fetchProfile };
}

/**
 * Hook to fetch a single patient's details by ID (for professional viewing a patient).
 *
 * @param {string|number} id - Patient ID
 * @returns {{patient: Object|null, isLoading: boolean, error: Error|null, refresh: Function}}
 */
export function usePatientDetail(id) {
  const [patient, setPatient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPatient = useCallback(async () => {
    if (!id) return; // Don't fetch if no ID yet
    try {
      setIsLoading(true);
      setError(null);
      const response = await patientService.getById(id);
      setPatient(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [id]); // Re-fetch when ID changes

  useEffect(() => {
    fetchPatient();
  }, [fetchPatient]);

  return { patient, isLoading, error, refresh: fetchPatient };
}

/**
 * Hook for the patient dashboard — fetches patient-specific stats.
 *
 * @returns {{stats: Object|null, isLoading: boolean, error: Error|null, refresh: Function}}
 */
export function usePatientDashboard() {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await patientService.getMyStats();
      setStats(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, isLoading, error, refresh: fetchStats };
}

/**
 * Hook to fetch the professionals that a patient has visited.
 *
 * @returns {{professionals: Array, isLoading: boolean, error: Error|null, refresh: Function}}
 */
export function usePatientProfessionals() {
  const [professionals, setProfessionals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfessionals = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await patientService.getMyProfessionals();
      setProfessionals(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfessionals();
  }, [fetchProfessionals]);

  return { professionals, isLoading, error, refresh: fetchProfessionals };
}
